import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ExternalLink, Check, ChevronDown,
  Wine, Globe, Building2, GraduationCap,
  Frame, Gamepad2, BookOpen, Sparkles, Users, Mail,
  TrendingUp, MapPin, ShieldCheck, Compass,
} from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { useT } from "@/lib/i18n";

const DEMO_URL = "https://calendar.app.google/9ghfPJuKTf7fU6sS9";
const CONTACT_MAILTO = "mailto:hello@geovinum.io?subject=geoVINUM%20Business";

const WHO = [
  { key: "wineries", Icon: Wine },
  { key: "importers", Icon: Globe },
  { key: "regions", Icon: Building2 },
  { key: "education", Icon: GraduationCap },
];

const BENEFITS = [
  { key: "storytelling", Icon: BookOpen },
  { key: "engagement", Icon: Sparkles },
  { key: "sales", Icon: TrendingUp },
  { key: "education", Icon: GraduationCap },
];

const SOLUTIONS = [
  {
    slug: "magnum", Icon: Compass, image: "/images/magnum.png",
    ctaKey: "biz.shared.learn_more",
    to: "/atlas/magnum",
    status: "available",
    useCases: ["wset_students", "sommeliers", "wine_pros", "education"],
  },
  {
    slug: "pro", Icon: Building2, image: "/images/iframe.jpg",
    ctaKey: "biz.shared.learn_more",
    to: "/atlas/pro",
    status: "available",
    useCases: ["importers", "distributors", "merchants", "schools"],
  },
  {
    slug: "maps", Icon: Frame, image: "/images/iframe.jpg",
    ctaKey: "biz.shared.view_demo",
    to: "/products/iframe",
    status: "available",
    useCases: ["winery_websites", "wine_tourism", "edu_content", "regional_promo"],
  },
  {
    slug: "portfolio", Icon: Wine, image: "/images/portfolio.jpg",
    ctaKey: "biz.shared.join_waitlist",
    to: "/products/portfolio",
    status: "coming_soon",
    useCases: ["winery_visits", "qr_code", "storytelling", "brand_engagement"],
  },
  {
    slug: "tasting", Icon: Gamepad2, image: "/images/tasting.jpg",
    ctaKey: "biz.shared.join_waitlist",
    to: "/products/tasting",
    status: "coming_soon",
    useCases: ["events", "wine_fairs", "cellar_doors", "hospitality"],
  },
];

// Matrice "Quand · Qui · Pourquoi" pour les 5 produits B2B/Pro — i18n keys
const MATRIX = [
  { slug: "magnum", icon: Compass, to: "/atlas/magnum" },
  { slug: "pro", icon: Building2, to: "/atlas/pro" },
  { slug: "iframe", icon: Frame, to: "/products/iframe" },
  { slug: "portfolio", icon: Wine, to: "/products/portfolio", soon: true },
  { slug: "tasting", icon: Gamepad2, to: "/products/tasting", soon: true },
];

const USE_CASES = ["tradefair", "website", "export", "tourism", "training"];

const EXAMPLES = [
  { key: "map", image: "/images/cartes-interactives.jpg" },
  { key: "domain", image: "/images/iframe.jpg" },
  { key: "mobile", image: "/images/magnum.png" },
  { key: "sheet", image: "/images/tasting.jpg" },
];

const STATS = [
  { key: "regions", Icon: MapPin },
  { key: "wineries", Icon: Wine },
  { key: "maps", Icon: Frame },
  { key: "countries", Icon: Globe },
];

const FAQ_KEYS = ["orgs", "embed", "languages", "tourism", "education", "custom"];

const BusinessPage = () => {
  const t = useT();
  return (
    <Layout testId="page-business">
      <SEO title={t("biz.seo.title")} description={t("biz.hero.sub")} />

      {/* SECTION 1 — HERO */}
      <Hero t={t} />

      {/* BONUS — Why organisations choose geoVINUM (checklist band) */}
      <WhyBand t={t} />

      {/* SECTION 2 — WHO IS IT FOR? */}
      <Who t={t} />

      {/* SECTION 3 — THE PROBLEM */}
      <Problem t={t} />

      {/* SECTION 4 — BENEFITS */}
      <Benefits t={t} />

      {/* SECTION 5 — SOLUTIONS */}
      <Solutions t={t} />

      {/* SECTION 5b — Matrice « Quand · Qui · Pourquoi » */}
      <WhenWhoWhy t={t} />

      {/* SECTION 6 — USE CASES */}
      <UseCases t={t} />

      {/* SECTION 7 — EXAMPLES */}
      <Examples t={t} />

      {/* SECTION 8 — WHY GEOVINUM (positioning) */}
      <Positioning t={t} />

      {/* SECTION 9 — SOCIAL PROOF */}
      <SocialProof t={t} />

      {/* SECTION 10 — FAQ */}
      <Faq t={t} />

      {/* SECTION 11 — FINAL CTA */}
      <FinalCta t={t} />
    </Layout>
  );
};

const Hero = ({ t }) => (
  <section className="pt-10 md:pt-16 pb-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Breadcrumb items={[{ to: "/", label: t("common.home") }, { label: t("nav.business") }]} />
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow mb-4" data-testid="biz-hero-eyebrow">{t("nav.business")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-[color:var(--gv-ink)] mb-5" data-testid="biz-hero-title">
            {t("biz.hero.title_a")} <span className="italic text-[color:var(--gv-pink)]">{t("biz.hero.title_b")}</span>
          </h1>
          <p className="text-base md:text-lg leading-[1.55] text-[color:var(--gv-ink)]/75 max-w-2xl mb-7" data-testid="biz-hero-sub">
            {t("biz.hero.sub")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="biz-hero-cta-primary">
              {t("biz.hero.cta_primary")} <ArrowRight size={15} />
            </a>
            <a href="#use-cases" className="btn-secondary" data-testid="biz-hero-cta-secondary">
              {t("biz.hero.cta_secondary")}
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="aspect-square rounded-[6px] overflow-hidden border border-[color:var(--gv-line)] shadow-[0_40px_80px_-40px_rgba(46,80,103,0.4)]">
            <img src="/images/iframe.jpg" alt="geoVINUM business preview" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhyBand = ({ t }) => {
  const bullets = ["terroirs", "storytelling", "engagement", "education", "differentiate"];
  return (
    <section className="bg-[color:var(--gv-ink)] text-white py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="eyebrow text-[color:var(--gv-pink)] mb-4" data-testid="biz-why-eyebrow">
          {t("biz.why.eyebrow")}
        </div>
        <h2 className="font-display text-2xl md:text-4xl leading-tight mb-7 max-w-2xl" data-testid="biz-why-title">
          {t("biz.why.title")}
        </h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm md:text-base text-white/90" data-testid={`biz-why-bullet-${b}`}>
              <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[color:var(--gv-pink)] text-white shrink-0">
                <Check size={12} strokeWidth={3} />
              </span>
              <span>{t(`biz.why.bullet.${b}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Who = ({ t }) => (
  <section className="py-20 md:py-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead eyebrow={t("biz.who.eyebrow")} title={t("biz.who.title")} testId="biz-who" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-10">
        {WHO.map(({ key, Icon }) => (
          <div
            key={key}
            className="reveal group bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 hover:border-[color:var(--gv-pink)] transition-colors"
            data-testid={`biz-who-card-${key}`}
          >
            <div className="w-11 h-11 rounded-[4px] bg-[color:var(--gv-mist)] flex items-center justify-center text-[color:var(--gv-blue)] mb-5 group-hover:bg-[color:var(--gv-pink)] group-hover:text-white transition-colors">
              <Icon size={20} />
            </div>
            <h3 className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{t(`biz.who.${key}.title`)}</h3>
            <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`biz.who.${key}.desc`)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Problem = ({ t }) => (
  <section className="py-20 md:py-24 bg-[color:var(--gv-mist)]">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <div className="eyebrow mb-4" data-testid="biz-problem-eyebrow">{t("biz.problem.eyebrow")}</div>
      <h2 className="font-display text-3xl md:text-5xl leading-tight text-[color:var(--gv-ink)] mb-8" data-testid="biz-problem-title">
        {t("biz.problem.title")}
      </h2>
      <div className="space-y-5 text-base md:text-lg leading-relaxed text-[color:var(--gv-ink)]/75">
        <p>{t("biz.problem.p1")}</p>
        <p>{t("biz.problem.p2")}</p>
        <p className="text-[color:var(--gv-ink)] font-medium">{t("biz.problem.p3")}</p>
      </div>
    </div>
  </section>
);

const Benefits = ({ t }) => (
  <section className="py-20 md:py-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead eyebrow={t("biz.benefits.eyebrow")} title={t("biz.benefits.title")} testId="biz-benefits" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 mt-12">
        {BENEFITS.map(({ key, Icon }) => (
          <div key={key} className="reveal flex gap-5" data-testid={`biz-benefit-${key}`}>
            <div className="shrink-0 w-12 h-12 rounded-[4px] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] flex items-center justify-center">
              <Icon size={22} />
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl text-[color:var(--gv-ink)] mb-2">{t(`biz.benefits.${key}.title`)}</h3>
              <p className="text-sm md:text-base text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`biz.benefits.${key}.desc`)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Solutions = ({ t }) => (
  <section className="py-20 md:py-24 bg-[color:var(--gv-mist)]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead eyebrow={t("biz.solutions.eyebrow")} title={t("biz.solutions.title")} testId="biz-solutions" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        {SOLUTIONS.map((s) => (
          <div
            key={s.slug}
            className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden flex flex-col hover:border-[color:var(--gv-pink)] transition-colors"
            data-testid={`biz-solution-${s.slug}`}
          >
            <div className="aspect-[16/10] bg-[color:var(--gv-mist)] overflow-hidden">
              <img src={s.image} alt={`geoVINUM ${s.slug}`} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-blue)]">
                  <s.Icon size={13} /> geoVINUM {s.slug.charAt(0).toUpperCase() + s.slug.slice(1)}
                </div>
                {s.status === "coming_soon" && (
                  <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]">{t("biz.shared.coming_soon")}</span>
                )}
              </div>
              <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed mb-4">{t(`biz.solutions.${s.slug}.desc`)}</p>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">{t("biz.shared.use_cases_label")}</div>
              <ul className="text-sm text-[color:var(--gv-ink)]/80 mb-6 space-y-1.5">
                {s.useCases.map((uc) => (
                  <li key={uc} className="flex items-start gap-2">
                    <span className="text-[color:var(--gv-pink)] mt-1">•</span>
                    <span>{t(`biz.solutions.${s.slug}.use.${uc}`)}</span>
                  </li>
                ))}
              </ul>
              <Link to={s.to} className="btn-secondary mt-auto justify-center" data-testid={`biz-solution-cta-${s.slug}`}>
                {t(s.ctaKey)} <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const UseCases = ({ t }) => (
  <section id="use-cases" className="py-20 md:py-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead eyebrow={t("biz.usecases.eyebrow")} title={t("biz.usecases.title")} testId="biz-usecases" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {USE_CASES.map((k, idx) => (
          <div
            key={k}
            className={`reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 ${idx === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}
            data-testid={`biz-usecase-${k}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-3">{`0${idx + 1}`}</div>
            <h3 className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{t(`biz.usecases.${k}.title`)}</h3>
            <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`biz.usecases.${k}.desc`)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Examples = ({ t }) => (
  <section className="py-20 md:py-24 bg-[color:var(--gv-mist)]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHead eyebrow={t("biz.examples.eyebrow")} title={t("biz.examples.title")} testId="biz-examples" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
        {EXAMPLES.map((e) => (
          <figure key={e.key} className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden" data-testid={`biz-example-${e.key}`}>
            <div className="aspect-[16/10] bg-[color:var(--gv-mist)] overflow-hidden">
              <img src={e.image} alt={t(`biz.examples.${e.key}`)} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <figcaption className="px-5 py-4 text-sm text-[color:var(--gv-ink)]/80 border-t border-[color:var(--gv-line)]">
              {t(`biz.examples.${e.key}`)}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const Positioning = ({ t }) => (
  <section className="py-20 md:py-24">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <div className="eyebrow mb-4" data-testid="biz-position-eyebrow">{t("biz.position.eyebrow")}</div>
      <h2 className="font-display text-3xl md:text-5xl leading-tight text-[color:var(--gv-ink)] mb-8" data-testid="biz-position-title">
        {t("biz.position.title")}
      </h2>
      <div className="space-y-5 text-base md:text-lg leading-relaxed text-[color:var(--gv-ink)]/75 max-w-3xl">
        <p>{t("biz.position.p1")}</p>
        <p>{t("biz.position.p2")}</p>
      </div>
    </div>
  </section>
);

const SocialProof = ({ t }) => (
  <section className="py-20 md:py-24 bg-[color:var(--gv-blue)] text-white grain">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center mb-12">
        <div className="eyebrow text-[color:var(--gv-pink)] mb-3" data-testid="biz-social-eyebrow">{t("biz.social.eyebrow")}</div>
        <h2 className="font-display text-3xl md:text-5xl" data-testid="biz-social-title">{t("biz.social.title")}</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        {STATS.map(({ key, Icon }) => (
          <div key={key} className="text-center" data-testid={`biz-stat-${key}`}>
            <Icon size={22} className="mx-auto mb-3 text-[color:var(--gv-pink)]" />
            <div className="font-display text-4xl md:text-5xl text-white mb-1">{t(`biz.social.stat.${key}.value`)}</div>
            <div className="text-xs uppercase tracking-widest text-white/70">{t(`biz.social.stat.${key}.label`)}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faq = ({ t }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 md:py-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3" data-testid="biz-faq-eyebrow">{t("biz.faq.eyebrow")}</div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)]" data-testid="biz-faq-title">{t("biz.faq.title")}</h2>
        </div>
        <div className="divide-y divide-[color:var(--gv-line)] border-y border-[color:var(--gv-line)]">
          {FAQ_KEYS.map((k, i) => {
            const isOpen = open === i;
            return (
              <div key={k} className="py-1">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                  aria-expanded={isOpen}
                  data-testid={`biz-faq-q-${k}`}
                >
                  <span className="font-display text-base md:text-lg text-[color:var(--gv-ink)]">{t(`biz.faq.${k}.q`)}</span>
                  <ChevronDown size={18} className={`text-[color:var(--gv-ink)]/50 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="pb-6 text-sm md:text-base text-[color:var(--gv-ink)]/75 leading-relaxed" data-testid={`biz-faq-a-${k}`}>
                    {t(`biz.faq.${k}.a`)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCta = ({ t }) => (
  <section className="py-24 md:py-28 bg-[color:var(--gv-pink)] text-white">
    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-display text-3xl md:text-5xl mb-4" data-testid="biz-final-title">{t("biz.final.title")}</h2>
      <p className="text-base md:text-lg text-white/85 mb-8 leading-relaxed max-w-xl mx-auto" data-testid="biz-final-body">
        {t("biz.final.body")}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[color:var(--gv-pink)] px-6 py-3 rounded-[4px] font-medium hover:bg-white/90 transition-colors" data-testid="biz-final-cta-primary">
          {t("biz.final.cta_primary")} <ArrowRight size={15} />
        </a>
        <a href={CONTACT_MAILTO} className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-[4px] font-medium hover:bg-white/10 transition-colors" data-testid="biz-final-cta-secondary">
          <Mail size={15} /> {t("biz.final.cta_secondary")}
        </a>
      </div>
    </div>
  </section>
);

const WhenWhoWhy = ({ t }) => (
  <section className="py-20 md:py-24 bg-white" data-testid="biz-matrix-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center mb-12">
        <div className="eyebrow mb-3" data-testid="biz-matrix-eyebrow">{t("bizM.eyebrow")}</div>
        <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)]" data-testid="biz-matrix-title">
          {t("bizM.title")}
        </h2>
        <p className="mt-4 text-[15px] text-[color:var(--gv-ink)]/70 max-w-2xl mx-auto">
          {t("bizM.subtitle")}
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block overflow-hidden border border-[color:var(--gv-line)] rounded-[5px]">
        <table className="w-full text-[13.5px] text-[color:var(--gv-ink)]" data-testid="biz-matrix-table">
          <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/65">
            <tr>
              <th className="text-left px-5 py-4 font-medium">{t("bizM.col.solution")}</th>
              <th className="text-left px-5 py-4 font-medium">{t("bizM.col.when")}</th>
              <th className="text-left px-5 py-4 font-medium">{t("bizM.col.who")}</th>
              <th className="text-left px-5 py-4 font-medium">{t("bizM.col.why")}</th>
              <th className="text-right px-5 py-4 font-medium">{t("bizM.col.action")}</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {MATRIX.map((row, idx) => {
              const Icon = row.icon;
              return (
                <tr key={row.slug} className={`${idx > 0 ? "border-t border-[color:var(--gv-line)]" : ""} hover:bg-[color:var(--gv-mist)]/40 transition-colors`} data-testid={`biz-matrix-row-${row.slug}`}>
                  <td className="px-5 py-5 align-top">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={15} className="text-[color:var(--gv-pink)]" />
                      <span className="font-display text-[15px]">{t(`bizM.${row.slug}.name`)}</span>
                    </div>
                    {row.soon && <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]">{t("bizM.soon")}</span>}
                  </td>
                  <td className="px-5 py-5 align-top text-[13px] text-[color:var(--gv-ink)]/80 leading-relaxed">{t(`bizM.${row.slug}.when`)}</td>
                  <td className="px-5 py-5 align-top text-[13px] text-[color:var(--gv-ink)]/80 leading-relaxed">{t(`bizM.${row.slug}.who`)}</td>
                  <td className="px-5 py-5 align-top text-[13px] text-[color:var(--gv-ink)]/80 leading-relaxed">{t(`bizM.${row.slug}.why`)}</td>
                  <td className="px-5 py-5 align-top text-right whitespace-nowrap">
                    <Link to={row.to} className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-pink)] hover:underline" data-testid={`biz-matrix-cta-${row.slug}`}>
                      {t("bizM.discover")} <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-4">
        {MATRIX.map((row) => {
          const Icon = row.icon;
          return (
            <div key={row.slug} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid={`biz-matrix-card-${row.slug}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={15} className="text-[color:var(--gv-pink)]" />
                <span className="font-display text-[16px]">{t(`bizM.${row.slug}.name`)}</span>
                {row.soon && <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] ml-auto">{t("bizM.soon")}</span>}
              </div>
              <Item label={t("bizM.label.when")} value={t(`bizM.${row.slug}.when`)} />
              <Item label={t("bizM.label.who")} value={t(`bizM.${row.slug}.who`)} />
              <Item label={t("bizM.label.why")} value={t(`bizM.${row.slug}.why`)} />
              <Link to={row.to} className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-pink)] hover:underline">
                {t("bizM.discover")} <ArrowRight size={12} />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Item = ({ label, value }) => (
  <div className="mb-2 last:mb-0">
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</div>
    <div className="text-[13px] text-[color:var(--gv-ink)]/80 leading-relaxed">{value}</div>
  </div>
);

const SectionHead = ({ eyebrow, title, testId }) => (
  <div className="max-w-3xl">
    {eyebrow && <div className="eyebrow mb-3" data-testid={`${testId}-eyebrow`}>{eyebrow}</div>}
    <h2 className="font-display text-3xl md:text-5xl leading-tight text-[color:var(--gv-ink)]" data-testid={`${testId}-title`}>
      {title}
    </h2>
  </div>
);

export default BusinessPage;
